import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import api from "../../api.js";
import "../../CSS/AreaMaster.css";
import { useRights } from "../../Components/RightsContext.jsx";

// Must match this screen's Submenus.pathname exactly — verify against the
// DB before trusting this (a mismatched string silently hides everything).
const Area_PATH = "/Master/AreaMaster";

// ── TOAST HOOK ─────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState([]);
  const toast = useCallback((msg, type = "info") => {
    const icons = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
    const id = Date.now() + Math.random();
    setToasts((p) => [...p, { id, msg, type, icon: icons[type] || "ℹ" }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 2800);
  }, []);
  return { toasts, toast };
}

function ToastWrap({ toasts }) {
  return (
    <div className="am-toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`am-toast ${t.type}`}>
          <span style={{ fontSize: 15 }}>{t.icon}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ── SVG ICONS ──────────────────────────────────────────────────
const IconPin     = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IconArea    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>;
const IconBox     = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IconCheck2  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const IconSearch  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconPlus    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>;
const IconEdit    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconTrash   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/></svg>;
const IconCheck   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>;

const STATES = [
  { name: "Maharashtra", code: "27" }, { name: "Karnataka", code: "29" },
  { name: "Tamil Nadu",  code: "33" }, { name: "Delhi",     code: "07" },
  { name: "Gujarat",     code: "24" }, { name: "Telangana", code: "36" },
  { name: "Rajasthan",   code: "08" },
];

const BRANCHES = ["MUM", "PUN", "CHN", "BLR", "DEL"];

/* ─── AutoComplete / ComboBox — matches RateDefinition style ─── */
function ComboBox({ id, value, onChange, options = [], placeholder = "Search…", className = "", disabled = false, valueKey = "value", labelKey = "label" }) {
  const [open,   setOpen]   = useState(false);
  const [query,  setQuery]  = useState("");
  const [focIdx, setFocIdx] = useState(-1);
  const wrapRef    = useRef(null);
  const inputRef   = useRef(null);
  const dropListRef = useRef(null);

  useEffect(() => {
    if (!open || focIdx < 0 || !dropListRef.current) return;
    const el = dropListRef.current.querySelector(`[data-idx="${focIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [focIdx, open]);

  // Normalise to { value, label } — case-insensitive key lookup
  const opts = useMemo(() => options.map((o) => {
    const keys = Object.keys(o);
    const vKey = keys.find((k) => k.toLowerCase() === valueKey.toLowerCase()) || valueKey;
    const lKey = keys.find((k) => k.toLowerCase() === labelKey.toLowerCase()) || labelKey;
    return {
      value: String(o[vKey] ?? o.value ?? o.id ?? ""),
      label: String(o[lKey] ?? o.label ?? o.name ?? ""),
    };
  }), [options, valueKey, labelKey]);

  const selected = opts.find((o) => o.value === String(value ?? ""));

  const highlight = (text, q) => {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return <>{text.slice(0, idx)}<mark>{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>;
  };

  const filtered = query.trim()
    ? opts.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : opts;

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) closeBox(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openBox = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setFocIdx(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const closeBox = () => { setOpen(false); setQuery(""); setFocIdx(-1); };

  const pick = (val) => {
    onChange({ target: { value: val } });
    closeBox();
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setFocIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (focIdx >= 0 && filtered[focIdx]) pick(filtered[focIdx].value); else if (filtered.length === 1) pick(filtered[0].value); }
    else if (e.key === "Escape") closeBox();
  };

  // Fixed positioning so the dropdown escapes any parent overflow:hidden
  const [dropStyle, setDropStyle] = useState({});
  const triggerRef = useRef(null);

  const updateDropPos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setDropStyle({
      position: "fixed",
      top: r.bottom + 4,
      left: r.left,
      width: r.width,
      zIndex: 99999,
    });
  }, []);

  useEffect(() => {
    if (open) {
      updateDropPos();
      window.addEventListener("scroll", updateDropPos, true);
      window.addEventListener("resize", updateDropPos);
    }
    return () => {
      window.removeEventListener("scroll", updateDropPos, true);
      window.removeEventListener("resize", updateDropPos);
    };
  }, [open, updateDropPos]);

  return (
    <div className="cb-ac" ref={wrapRef} id={id}>
      <div ref={triggerRef} className={`cb-ac-box${open ? " open" : ""}${disabled ? " cb-disabled" : ""}`} onClick={openBox}>
        <input
          ref={inputRef}
          className={`cb-ac-in${className ? " " + className : ""}`}
          type="text"
          placeholder={selected ? selected.label : placeholder}
          value={open ? query : (selected ? selected.label : "")}
          onChange={(e) => { setQuery(e.target.value); setFocIdx(-1); }}
          onKeyDown={handleKey}
          readOnly={!open}
          disabled={disabled}
          style={{ cursor: disabled ? "not-allowed" : open ? "text" : "pointer" }}
        />
        {selected && !open && (
          <span className="cb-ac-x on" onMouseDown={(e) => { e.stopPropagation(); pick(""); }}>×</span>
        )}
        <span className="cb-ac-chev">▾</span>
      </div>

      {open && ReactDOM.createPortal(
        <div className="cb-ac-drop open" style={dropStyle} ref={dropListRef}>
          {filtered.length === 0
            ? <div className="cb-ac-empty">No results found</div>
            : filtered.map((o, i) => (
              <div
                key={o.value}
                data-idx={i}
                className={`cb-ac-opt${o.value === String(value ?? "") ? " sel" : ""}${i === focIdx ? " foc" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
              >
                {highlight(o.label, query)}
              </div>
            ))
          }
        </div>,
        document.body
      )}
    </div>
  );
}

// PlaceSelect — combobox bound to the place_id / place_Name shape used by placesDropdown
function PlaceSelect({ id, value, onChange, options, placeholder, className, disabled = false }) {
  return (
    <ComboBox
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder || "Search place…"}
      className={className}
      disabled={disabled}
      valueKey="place_id"
      labelKey="place_Name"
    />
  );
}

// ── MAIN COMPONENT ─────────────────────────────────────────────
export default function AreaMaster() {
  const rights = useRights(Area_PATH);
  // ── DATA ──
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── SELECTION ──
  const [selPlace, setSelPlace] = useState("");
  const [placeSearch, setPlaceSearch] = useState("");
  const [areaSearch,  setAreaSearch]  = useState("");
  const [placePage, setPlacePage] = useState(1);
const PLACES_PER_PAGE = 8;
  // ── PLACE MODAL ──
  const [placeModalOpen, setPlaceModalOpen] = useState(false);
  const [editPlaceCode,  setEditPlaceCode]  = useState(null);
  const [pmName,    setPmName]    = useState("");
  const [pmCode,    setPmCode]    = useState("");
  const [pmState,   setPmState]   = useState("");
  const [pmBranch,  setPmBranch]  = useState("");
  const [pmActive,  setPmActive]  = useState("true");
  const [pmErrName, setPmErrName] = useState(false);
  const [pmErrCode, setPmErrCode] = useState(false);
  const pmNameRef = useRef(null);
  const [placesDropdown, setPlacesDropdown] = useState([]);
const [amPlaceId, setAmPlaceId] = useState("");
const [areas, setAreas] = useState([]);
  // ── AREA MODAL ──
  const [areaModalOpen, setAreaModalOpen] = useState(false);
  const [editAreaCode,  setEditAreaCode]  = useState(null);
  const [amName,    setAmName]    = useState("");
  const [amCode,    setAmCode]    = useState("");
  const [amActive,  setAmActive]  = useState("true");
  const [amPinInput, setAmPinInput] = useState("");
  const [modalPins,  setModalPins]  = useState([]);
  const [amErrName, setAmErrName] = useState(false);
  const [amErrPin,  setAmErrPin]  = useState(false);
  const amNameRef = useRef(null);

  const { toasts, toast } = useToast();

  // ── INIT ──────────────────────────────────────────────────────
  useEffect(() => {
    loadPlaces();
  }, []);

  const loadPlaces = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/area-master/places");
      const data = res.data || [];
      setPlaces(data);
      if (data.length && !selPlace) setSelPlace(data[0].code);
    } catch {
      toast("Failed to load places", "err");
    } finally {
      setLoading(false);
    }
  };


const splitPins = (pincodeStr) =>
  (pincodeStr || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

const loadAreas = async () => {
  try {
    const res = await api.get("/api/area-master/areas");

    const areaList = res.data || [];

    const grouped = areaList.reduce((acc, a) => {

      // Use placeId as the grouping key — unique per place, unlike branchPrefix
      const key = String(a.placeId ?? "unknown");

      if (!acc[key]) {
        acc[key] = {
          code: key,                 // unique key used for selPlace
          placeId: a.placeId,
          name: a.placeName || "Unknown Place",
          state: a.state || "",
          branch: a.branchCode || "",
          areas: []
        };
      }

      acc[key].areas.push({
        areaId: a.areaId,
        name: a.areaName,
        code: a.areaCode,
        active: a.isActive,
        pincodes: splitPins(a.pincodes),   // ← was always [] before
      });

      return acc;

    }, {});


    const placeArray = Object.values(grouped);

    setPlaces(placeArray);


    if (placeArray.length && !selPlace) {
      setSelPlace(placeArray[0].code);
    }


  } catch (err) {
    toast("Failed to load areas", "err");
  }
};

useEffect(() => {
  loadAreas();
  loadPlacesDropdown();
}, []);


const loadPlacesDropdown = async () => {
  try {
    const res = await api.get("/dropdown?mode=plca");
    setPlacesDropdown(res.data || []);
  } catch {
    setPlacesDropdown([]);
  }
};

  // ── DERIVED ──────────────────────────────────────────────────
  const curPlace = places.find((p) => p.code === selPlace) || null;

  const placePins = (p) => (p.areas || []).reduce((a, x) => a + (x.pincodes?.length || 0), 0);

  const stats = {
    places: places.length,
    areas:  places.reduce((a, p) => a + (p.areas?.length || 0), 0),
    pins:   places.reduce((a, p) => a + placePins(p), 0),
    active: places.reduce((a, p) => a + (p.areas || []).filter((x) => x.active).length, 0),
  };

const filteredPlaces = places.filter((p) => {
  const q = placeSearch.toLowerCase().trim();
  if (!q) return true;
  return p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q) || (p.state || "").toLowerCase().includes(q);
});

const placePages = Math.max(1, Math.ceil(filteredPlaces.length / PLACES_PER_PAGE));
const curPlacePage = Math.min(placePage, placePages);
const pagedPlaces = filteredPlaces.slice(
  (curPlacePage - 1) * PLACES_PER_PAGE,
  curPlacePage * PLACES_PER_PAGE
);

  const filteredAreas = curPlace
    ? (curPlace.areas || []).filter((a) => {
        const q = areaSearch.toLowerCase().trim();
        if (!q) return true;
        return a.name.toLowerCase().includes(q) || a.code.toLowerCase().includes(q) || (a.pincodes || []).some((pc) => pc.includes(q));
      })
    : [];

  // ── PLACE MODAL ───────────────────────────────────────────────
  const openPlaceModal = (code = null) => {
    setEditPlaceCode(code);
    const p = code ? places.find((x) => x.code === code) : null;
    setPmName(p ? p.name : "");
    setPmCode(p ? p.code : "");
    setPmState(p ? p.stateCode : "");
    setPmBranch(p ? p.branch : "");
    setPmActive(p ? String(p.active) : "true");
    setPmErrName(false);
    setPmErrCode(false);
    setPlaceModalOpen(true);
    setTimeout(() => pmNameRef.current?.focus(), 100);
  };

  const closePlaceModal = () => setPlaceModalOpen(false);

  const savePlace = async () => {
    const name = pmName.trim();
    const code = pmCode.trim().toUpperCase();
    let ok = true;

    if (!name) { setPmErrName(true); ok = false; }
    if (!editPlaceCode && (!code || places.some((p) => p.code === code))) { setPmErrCode(true); ok = false; }
    if (!pmState)  { toast("Select a state", "warn");  ok = false; }
    if (!pmBranch) { toast("Select a branch", "warn"); ok = false; }
    if (!ok) return;

    const st = STATES.find((s) => s.code === pmState);
    const payload = {
      code, name,
      state: st?.name, stateCode: pmState,
      branch: pmBranch,
      active: pmActive === "true",
    };

    try {
      if (editPlaceCode) {
        await api.post(`/api/area-master/places/${editPlaceCode}`, payload);
        toast("Place updated", "ok");
      } else {
        await api.post("/api/area-master/places", payload);
        setSelPlace(code);
        toast(`Place "${name}" added`, "ok");
      }
      closePlaceModal();
      await loadPlaces();
    } catch (err) {
      toast(err.response?.data?.message || "Save failed", "err");
    }
  };

  // ── AREA MODAL ────────────────────────────────────────────────
  const nextAreaCode = (p) => {
    let n = 1;
    const used = new Set((p.areas || []).map((a) => a.code));
    while (used.has(`AR-${p.code}-${String(n).padStart(2, "0")}`)) n++;
    return `AR-${p.code}-${String(n).padStart(2, "0")}`;
  };

//   const openAreaModal = (area = null) => {
//   setEditAreaCode(area ? area.areaId : null);
//   setAmName(area ? area.areaName : "");
//   setAmCode(area ? area.areaCode : "");
//   setAmActive(area ? String(area.isActive) : "true");
//   setAmPlaceId(area ? String(area.placeId || "") : "");
//   setAmErrName(false);
//   setAreaModalOpen(true);
//   setTimeout(() => amNameRef.current?.focus(), 120);
// };
const openAreaModal = (area = null) => {
  setEditAreaCode(area ? area.areaId : null);
  setAmName(area ? area.name : "");
  setAmCode(area ? area.code : "");
  setAmActive(area ? String(area.active) : "true");
  // placeId comes from curPlace, not the area object itself
  setAmPlaceId(area && curPlace ? String(curPlace.placeId || "") : "");
  setAmPinInput("");
  setModalPins(area ? area.pincodes : []);   // already an array, no need to split
  setAmErrName(false);
  setAmErrPin(false);
  setAreaModalOpen(true);
  setTimeout(() => amNameRef.current?.focus(), 120);
};
  const closeAreaModal = () => setAreaModalOpen(false);

  const addPin = () => {
    const v = amPinInput.trim();
    if (!/^\d{6}$/.test(v)) { toast("Enter a valid 6-digit pincode", "warn"); return; }
    if (modalPins.includes(v)) { toast("Pincode already added", "warn"); setAmPinInput(""); return; }
    setModalPins((prev) => [...prev, v]);
    setAmPinInput("");
    setAmErrPin(false);
  };

  const rmPin = (pc) => setModalPins((prev) => prev.filter((x) => x !== pc));


const saveArea = async () => {
  // const name = amName.trim();
  const name = (amName || "").trim();
  let ok = true;

  if (!name) { setAmErrName(true); ok = false; }
  if (!amPlaceId) { alert("Select a place", "warn"); ok = false; }
  if (modalPins.length === 0) { setAmErrPin(true); toast("Add at least one pincode", "warn"); ok = false; }
  if (!ok) return;

  const placeObj = placesDropdown.find((p) => String(p.place_id) === String(amPlaceId));

  const payload = {
    areaName:   name,
    areaCode:   amCode.trim().toUpperCase() || null,
    branchCode: placeObj?.Branch_Prefix || null,
    isActive:   amActive === "true",
    placeId:    parseInt(amPlaceId, 10),
    // pincode: amPinInput.trim() ? [amPinInput.trim()] : [],
    pincode:  modalPins.join(","),
  };

  try {
    if (editAreaCode) {
      await api.post(`/api/area-master/areas/update/${editAreaCode}`, payload);
      toast("Area updated", "ok");
      alert("Area updated successfully");
    } else {
      await api.post("/api/area-master/areas", payload);
      toast(`Area "${name}" added`, "ok");
      alert("Area added successfully");
    }
    closeAreaModal();
    await loadAreas();   // ← reload flat list
  } catch (err) {
    const rawMsg = err.response?.data?.message || err.message || "";
    const isDuplicate = /UNIQUE KEY|duplicate key/i.test(rawMsg);
    const friendlyMsg = isDuplicate
      ? "Area already existed in selected place."
      : (err.response?.data?.message || "Save failed");

    toast(friendlyMsg, "err");
    alert(friendlyMsg);
  }
};
  const delArea = async (areaId, name) => {
    if (!window.confirm(`Delete area "${name}"?`)) return;

    try {
      await api.post(`/api/area-master/areas/delete/${areaId}`);
      toast("Area deleted", "info");
      alert(`Area "${name}" deleted successfully`);
      await loadAreas();
    } catch (err) {
      toast(err.response?.data?.message || "Delete failed", "err");
      alert("Error deleting area: " + (err.response?.data?.message || err.message));
    }
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="am-root">
      <div className="am-page">

        {/* Header */}
        <div className="am-pg-row">
          <div className="am-pg-title">
            <h1>Area Master</h1>
            <p>Manage delivery places, their areas, and the pincodes served under each area</p>
          </div>
        </div>

        {/* Stats */}
        <div className="am-stat-row">
          <div className="am-stat-box"><div className="am-sb-ico navy"><IconPin /></div><div><div className="am-sb-val">{stats.places}</div><div className="am-sb-lbl">Places</div></div></div>
          <div className="am-stat-box"><div className="am-sb-ico teal"><IconArea /></div><div><div className="am-sb-val">{stats.areas}</div><div className="am-sb-lbl">Areas</div></div></div>
          <div className="am-stat-box"><div className="am-sb-ico blue"><IconBox /></div><div><div className="am-sb-val">{stats.pins}</div><div className="am-sb-lbl">Pincodes</div></div></div>
          <div className="am-stat-box"><div className="am-sb-ico green"><IconCheck2 /></div><div><div className="am-sb-val">{stats.active}</div><div className="am-sb-lbl">Active Areas</div></div></div>
        </div>

        <div className="am-master-grid">

          {/* ── PLACES PANEL ── */}
          <div className="am-card">
            <div className="am-band navy-band">
              <div className="am-band-icon"><IconPin /></div>
              <div><div className="am-band-title">Places</div><div className="am-band-sub">Cities / locations</div></div>
              <div className="am-band-r"><span className="am-band-badge">{places.length}</span></div>
            </div>

            <div className="am-search-wrap">
              <div className="am-search-box">
                <IconSearch />
                <input
                  type="text"
                  placeholder="Search place / code…"
                  value={placeSearch}
                  // onChange={(e) => setPlaceSearch(e.target.value)}
                  onChange={(e) => { setPlaceSearch(e.target.value); setPlacePage(1); }}
                />
              </div>
            </div>

            {/* <div className="am-places-list">
              {loading ? (
                <div className="am-empty" style={{ padding: "30px 14px" }}>
                  <div className="am-empty-title" style={{ fontSize: 13 }}>Loading…</div>
                </div>
              ) : filteredPlaces.length === 0 ? (
                <div className="am-empty" style={{ padding: "30px 14px" }}>
                  <div className="am-empty-ico">🔍</div>
                  <div className="am-empty-title" style={{ fontSize: 13 }}>No places</div>
                </div>
              ) : filteredPlaces.map((p) => (
                <div
                  key={p.code}
                  className={`am-place-item${p.code === selPlace ? " active" : ""}`}
                  onClick={() => setSelPlace(p.code)}
                >
                  <div className="am-pi-top">
                    <span className="am-pi-name">{p.name}</span>
                    <span className="am-pi-code">{p.code}</span>
                  </div>
                  <div className="am-pi-state">📍 {p.state} · Branch {p.branch}</div>
                  <div className="am-pi-metrics">
                    <span className="am-pi-metric"><b>{(p.areas || []).length}</b> areas</span>
                    <span className="am-pi-metric"><b>{placePins(p)}</b> pincodes</span>
                  </div>
                </div>
              ))}
            </div> */}
           
            <div className="am-places-list">
                {loading ? (
                  <div className="am-empty" style={{ padding: "30px 14px" }}>
                    <div className="am-empty-title" style={{ fontSize: 13 }}>Loading…</div>
                  </div>
                ) : filteredPlaces.length === 0 ? (
                  <div className="am-empty" style={{ padding: "30px 14px" }}>
                    <div className="am-empty-ico">🔍</div>
                    <div className="am-empty-title" style={{ fontSize: 13 }}>No places</div>
                  </div>
                ) : pagedPlaces.map((p) => (
                  <div
                    key={p.code}
                    className={`am-place-item${p.code === selPlace ? " active" : ""}`}
                    onClick={() => setSelPlace(p.code)}
                  >
                    <div className="am-pi-top">
                      <span className="am-pi-name">{p.name}</span>
                      <span className="am-pi-code">{p.code}</span>
                    </div>
                    <div className="am-pi-state">📍 {p.state} · Branch {p.branch}</div>
                    <div className="am-pi-metrics">
                      <span className="am-pi-metric"><b>{(p.areas || []).length}</b> areas</span>
                      <span className="am-pi-metric"><b>{placePins(p)}</b> pincodes</span>
                    </div>
                  </div>
                ))}
            </div>

            {!loading && filteredPlaces.length > 0 && placePages > 1 && (
              <div className="am-place-pager">
                <button
                  className="am-pp-btn"
                  disabled={curPlacePage === 1}
                  onClick={() => setPlacePage((p) => Math.max(1, p - 1))}
                >
                  ‹ Prev
                </button>
                <span className="am-pp-info">Page {curPlacePage} of {placePages}</span>
                <button
                  className="am-pp-btn"
                  disabled={curPlacePage === placePages}
                  onClick={() => setPlacePage((p) => Math.min(placePages, p + 1))}
                >
                  Next ›
                </button>
              </div>
            )}
          </div>

          {/* ── AREAS PANEL ── */}
          <div className="am-card">
            <div className="am-band teal-band">
              <div className="am-band-icon"><IconArea /></div>
              <div>
                <div className="am-band-title">Areas</div>
                <div className="am-band-sub">{curPlace ? `${curPlace.name} · ${curPlace.state}` : "Select a place"}</div>
              </div>
              <div className="am-band-r"><span className="am-band-badge">{curPlace ? `${curPlace.areas.length} areas` : "0"}</span></div>
            </div>

            <div className="am-area-toolbar">
              <div className="am-search-box am-area-search">
                <IconSearch />
                <input
                  type="text"
                  placeholder="Search area / pincode…"
                  value={areaSearch}
                  onChange={(e) => setAreaSearch(e.target.value)}
                />
              </div>
              {/* <button className="am-btn am-btn-primary" disabled={!curPlace} onClick={() => openAreaModal()}>
                <IconPlus /> Add Area
              </button> */}
              {rights.add && (
              <button className="am-btn am-btn-primary" onClick={() => openAreaModal()}>
                <IconPlus /> Add Area
              </button>
              )}
            </div>

            {!curPlace ? (
              <div className="am-empty">
                <div className="am-empty-ico">🗺️</div>
                <div className="am-empty-title">No place selected</div>
                <div className="am-empty-sub">Select a place from the left to view its areas</div>
              </div>
            ) : filteredAreas.length === 0 ? (
              <div className="am-empty">
                <div className="am-empty-ico">🗺️</div>
                <div className="am-empty-title">{curPlace.areas.length ? "No matching areas" : "No areas yet"}</div>
                <div className="am-empty-sub">{curPlace.areas.length ? "Try another search" : "Add an area with its pincodes to get started"}</div>
              </div>
            ) : (
              <div className="am-area-grid">
                {filteredAreas.map((a) => (
                  <div className="am-area-card" key={a.areaId}>
                    <div className="am-ac-head">
                      <div>
                        <div className="am-ac-name">{a.name}</div>
                        <div className="am-ac-code">{a.code}</div>
                      </div>
                      <span className={`am-status-pill ${a.active ? "am-st-active" : "am-st-inactive"}`}>
                        {a.active ? "● Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="am-ac-body">
                      <div className="am-ac-pin-lbl"><IconBox /> Pincodes ({a.pincodes.length})</div>
                      <div className="am-pin-chips">
                        {a.pincodes.map((pc) => <span className="am-pin-chip" key={pc}>{pc}</span>)}
                      </div>
                    </div>
                    <div className="am-ac-foot">
                      <span className="am-ac-pincount"><b>{a.pincodes.length}</b> pincode{a.pincodes.length !== 1 ? "s" : ""}</span>
                      <div className="am-ac-acts">
                        {rights.edit && (<button className="am-ic-btn am-ic-edit" onClick={() => openAreaModal(a)}><IconEdit /></button>)}
                        {/* {rights.delete && (<button className="am-ic-btn am-ic-del" onClick={() => delArea(a.areaId, a.name)}><IconTrash /></button>)} */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>{/* /am-page */}

      {/* ── PLACE MODAL ── */}
      {placeModalOpen && (
        <div className="am-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && closePlaceModal()}>
          <div className="am-modal">
            <div className="am-modal-head">
              <div className="am-mh-ico"><IconPin /></div>
              <div>
                <div className="am-mh-title">{editPlaceCode ? "Edit Place" : "Add Place"}</div>
                <div className="am-mh-sub">City / location master</div>
              </div>
              <button className="am-modal-x" onClick={closePlaceModal}>✕</button>
            </div>
            <div className="am-modal-body">
              <div className="am-g2">
                <div className="am-field">
                  <label>Place Name <span className="req">*</span></label>
                  <input
                    ref={pmNameRef}
                    type="text"
                    placeholder="e.g. Pune"
                    className={pmErrName ? "err" : ""}
                    value={pmName}
                    onChange={(e) => { setPmName(e.target.value); setPmErrName(false); }}
                  />
                  {pmErrName && <div className="am-field-err show">Required</div>}
                </div>
                <div className="am-field">
                  <label>Place Code <span className="req">*</span></label>
                  <input
                    type="text"
                    className={`mono${pmErrCode ? " err" : ""}`}
                    placeholder="e.g. PUN"
                    style={{ textTransform: "uppercase" }}
                    value={pmCode}
                    disabled={!!editPlaceCode}
                    onChange={(e) => { setPmCode(e.target.value); setPmErrCode(false); }}
                  />
                  {pmErrCode && <div className="am-field-err show">Required / duplicate</div>}
                </div>
              </div>
              <div className="am-g2">
                <div className="am-field">
                  <label>State <span className="req">*</span></label>
                  <select value={pmState} onChange={(e) => setPmState(e.target.value)}>
                    <option value="">Select…</option>
                    {STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
                  </select>
                </div>
                <div className="am-field">
                  <label>Branch <span className="req">*</span></label>
                  <select value={pmBranch} onChange={(e) => setPmBranch(e.target.value)}>
                    <option value="">Select…</option>
                    {BRANCHES.map((b) => <option key={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div className="am-field">
                <label>Status</label>
                <select value={pmActive} onChange={(e) => setPmActive(e.target.value)}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
            <div className="am-modal-foot">
              <button className="am-btn am-btn-ghost" onClick={closePlaceModal}>Cancel</button>
              <button className="am-btn am-btn-green" onClick={savePlace}><IconCheck /> Save Place</button>
            </div>
          </div>
        </div>
      )}

      {/* ── AREA MODAL ── */}
      {areaModalOpen && (
        <div className="am-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && closeAreaModal()}>
          <div className="am-modal">
            <div className="am-modal-head">
              <div className="am-mh-ico"><IconArea /></div>
              <div>
                <div className="am-mh-title">{editAreaCode ? "Edit Area" : "Add Area"}</div>
                <div className="am-mh-sub">{curPlace ? `Under ${curPlace.name} (${curPlace.code})` : "—"}</div>
              </div>
              <button className="am-modal-x" onClick={closeAreaModal}>✕</button>
            </div>
            <div className="am-modal-body">
              <div className="am-g2">
                <div className="am-field">
                  <label>Place <span className="req">*</span></label>
                  <PlaceSelect
                    id="amPlace"
                    value={amPlaceId}
                    onChange={(e) => setAmPlaceId(e.target.value)}
                    options={placesDropdown.map((p) => ({
                      place_id: p.place_id,
                      place_Name: `${p.place_Name} (${p.Branch_Prefix})`,
                    }))}
                    placeholder="Select place…"
                  />
                </div>
                <div className="am-field">
                  <label>Area Name <span className="req">*</span></label>
                  <input
                    ref={amNameRef}
                    type="text"
                    placeholder="e.g. Pune City"
                    className={amErrName ? "err" : ""}
                    value={amName}
                    onChange={(e) => { setAmName(e.target.value); setAmErrName(false); }}
                  />
                  {amErrName && <div className="am-field-err show">Required</div>}
                </div>
                <div className="am-field">
                  <label>Area Code</label>
                  <input
                    type="text"
                    className="mono"
                    placeholder={curPlace ? nextAreaCode(curPlace) : "auto"}
                    style={{ textTransform: "uppercase" }}
                    value={amCode}
                    disabled={!!editAreaCode}
                    onChange={(e) => setAmCode(e.target.value)}
                  />
                </div>
                <div className="am-field">
                  <label>Status</label>
                  <select value={amActive} onChange={(e) => setAmActive(e.target.value)}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="am-field" style={{ marginBottom: 6 }}>
                <label>Pincodes <span className="req">*</span> <span style={{ color: "var(--muted)", fontWeight: 600, textTransform: "none" }}>(one or more)</span></label>
                <div className="am-pin-entry">
                  <input
                    type="text"
                    className="mono"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit pincode"
                    value={amPinInput}
                    onChange={(e) => setAmPinInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPin(); } }}
                  />
                  <button className="am-btn am-btn-teal" onClick={addPin}><IconPlus /> Add</button>
                </div>
                <div className="am-pin-hint">Type a 6-digit pincode and press Enter or Add. At least one pincode is required.</div>
              </div>
              <div className={`am-pin-edit-box${amErrPin ? " err" : ""}`}>
                {modalPins.length === 0 ? (
                  <div className="am-pin-edit-empty">No pincodes added yet</div>
                ) : (
                  <div className="am-pin-edit-chips">
                    {modalPins.map((pc) => (
                      <span className="am-pin-edit-chip" key={pc}>
                        {pc}
                        <button onClick={() => rmPin(pc)} aria-label="Remove">✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {amErrPin && <div className="am-field-err show" style={{ marginTop: 6 }}>Add at least one pincode</div>}
            </div>
            <div className="am-modal-foot">
              <button className="am-btn am-btn-ghost" onClick={closeAreaModal}>Cancel</button>
              <button className="am-btn am-btn-green" onClick={saveArea}><IconCheck /> Save Area</button>
            </div>
          </div>
        </div>
      )}

      <ToastWrap toasts={toasts} />
    </div>
  );
}